/**
 * 通知类型常量配置
 * 统一管理通知类型、图标、颜色配置
 */

import type { Component } from 'vue'
import {
  Bell,
  CircleCheck,
  Clock,
  Warning,
  Box,
  Back,
  Folder,
  Tools
} from '@element-plus/icons-vue'

/**
 * 通知类型枚举
 */
export enum NotificationTypeEnum {
  SYSTEM = 'SYSTEM',
  APPROVAL = 'APPROVAL',
  TASK_REMINDER = 'TASK_REMINDER',
  WARNING = 'WARNING',
  BORROW = 'BORROW',
  RETURN = 'RETURN',
  STORAGE = 'STORAGE',
  PROCESSING = 'PROCESSING'
}

/**
 * 通知类型标签
 */
export const NotificationTypeLabels: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.SYSTEM]: '系统通知',
  [NotificationTypeEnum.APPROVAL]: '审批通知',
  [NotificationTypeEnum.TASK_REMINDER]: '任务提醒',
  [NotificationTypeEnum.WARNING]: '预警通知',
  [NotificationTypeEnum.BORROW]: '领用通知',
  [NotificationTypeEnum.RETURN]: '归还通知',
  [NotificationTypeEnum.STORAGE]: '入库通知',
  [NotificationTypeEnum.PROCESSING]: '加工通知'
}

/**
 * 通知类型图标映射
 * 使用 Element Plus 图标组件
 */
export const NotificationIconMap: Record<NotificationTypeEnum, Component> = {
  [NotificationTypeEnum.SYSTEM]: Bell,
  [NotificationTypeEnum.APPROVAL]: CircleCheck,
  [NotificationTypeEnum.TASK_REMINDER]: Clock,
  [NotificationTypeEnum.WARNING]: Warning,
  [NotificationTypeEnum.BORROW]: Box,
  [NotificationTypeEnum.RETURN]: Back,
  [NotificationTypeEnum.STORAGE]: Folder,
  [NotificationTypeEnum.PROCESSING]: Tools
}

/**
 * 通知类型颜色映射
 * 使用十六进制颜色值
 */
export const NotificationColorMap: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.SYSTEM]: '#2383e2',    // 蓝色
  [NotificationTypeEnum.APPROVAL]: '#27ae60',   // 绿色
  [NotificationTypeEnum.TASK_REMINDER]: '#e6a23c', // 橙色
  [NotificationTypeEnum.WARNING]: '#f56c6c',    // 红色
  [NotificationTypeEnum.BORROW]: '#909399',     // 灰色
  [NotificationTypeEnum.RETURN]: '#909399',     // 灰色
  [NotificationTypeEnum.STORAGE]: '#2383e2',    // 蓝色
  [NotificationTypeEnum.PROCESSING]: '#e6a23c'  // 橙色
}

/**
 * 通知类型背景色映射（用于图标背景）
 * 使用 rgba 格式，透明度 0.1
 */
export const NotificationBgColorMap: Record<NotificationTypeEnum, string> = {
  [NotificationTypeEnum.SYSTEM]: 'rgba(35, 131, 226, 0.1)',
  [NotificationTypeEnum.APPROVAL]: 'rgba(39, 174, 96, 0.1)',
  [NotificationTypeEnum.TASK_REMINDER]: 'rgba(230, 162, 60, 0.1)',
  [NotificationTypeEnum.WARNING]: 'rgba(245, 108, 108, 0.1)',
  [NotificationTypeEnum.BORROW]: 'rgba(144, 147, 153, 0.1)',
  [NotificationTypeEnum.RETURN]: 'rgba(144, 147, 153, 0.1)',
  [NotificationTypeEnum.STORAGE]: 'rgba(35, 131, 226, 0.1)',
  [NotificationTypeEnum.PROCESSING]: 'rgba(230, 162, 60, 0.1)'
}

/**
 * 获取通知类型图标
 * @param type 通知类型字符串
 * @returns 对应的图标组件
 */
export function getNotificationIcon(type: string): Component {
  return NotificationIconMap[type as NotificationTypeEnum] || Bell
}

/**
 * 获取通知类型颜色
 * @param type 通知类型字符串
 * @returns 对应的颜色值
 */
export function getNotificationColor(type: string): string {
  return NotificationColorMap[type as NotificationTypeEnum] || '#2383e2'
}

/**
 * 获取通知类型背景色
 * @param type 通知类型字符串
 * @returns 对应的背景色值
 */
export function getNotificationBgColor(type: string): string {
  return NotificationBgColorMap[type as NotificationTypeEnum] || 'rgba(35, 131, 226, 0.1)'
}

/**
 * 获取通知类型标签
 * @param type 通知类型字符串
 * @returns 对应的标签文本
 */
export function getNotificationTypeLabel(type: string): string {
  return NotificationTypeLabels[type as NotificationTypeEnum] || '未知类型'
}

/**
 * 后端通知类型映射到前端通知类型
 * 保持与 MainLayout.vue 的兼容性
 * @param backendType 后端通知类型（如 'BORROW', 'STORAGE' 等）
 * @returns 前端通知类型（如 'borrow', 'storage' 等）
 */
export function mapBackendNotificationType(backendType: string): string {
  const typeMapping: Record<string, string> = {
    'SYSTEM': 'system',
    'APPROVAL': 'check',
    'TASK_REMINDER': 'task',
    'WARNING': 'warning',
    'BORROW': 'borrow',
    'RETURN': 'return',
    'STORAGE': 'storage',
    'PROCESSING': 'processing'
  }
  return typeMapping[backendType] || 'system'
}
